import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://www.ifillip.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/logo512.png`; // Using existing logo instead of missing og-image.jpg
const SITE_NAME = "iFillip";
const TWITTER_HANDLE = "@ifillip";

/**
 * SEO component for page-level meta tags and structured data.
 *
 * Usage:
 *   <SEO
 *     title="Page Title"
 *     description="Page description"
 *     path="/page-path"
 *     schema={schemaObject}        // optional JSON-LD object or array
 *     canonicalUrl="https://..."    // optional override
 *     ogImage="https://..."         // optional per-page OG image
 *     noIndex={false}               // optional, default false
 *     keywords="comma, separated"   // optional
 *   />
 */
const SEO = ({
  title,
  description,
  path = "/",
  schema,
  canonicalUrl,
  ogImage,
  noIndex = false,
  keywords,
  ogType = "website",
  locale = "en_IN",
}) => {
  const canonical = canonicalUrl || `${SITE_URL}${path}`;
  const image = ogImage || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      {/* ── Primary Meta Tags ── */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* ── Open Graph / Facebook ── */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={locale} />

      {/* ── Twitter ── */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* ── Structured Data (JSON-LD) ── */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema, null, 2)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;